// Phase 7 D3 — Track C T-C4. DeviceRow component.
//
// Renders one row of /api/auth/devices output. Q-11: existing shape (kind:
// 'session' | 'refresh', label nullable, current flag). The "current"
// device gets a Badge and the Revoke button is hidden — the user signs
// itself out via the UserMenu, not via Revoke (which would bricking-loop
// the cookie).

import * as React from 'react';
import type { Device } from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../../../components/ui/Dialog.js';
import { Button } from '../../../components/ui/Button.js';
import { Badge } from '../../../components/ui/Badge.js';

interface DeviceRowProps {
  device: Device;
  onConfirmRevoke: (id: string) => void;
  isRevoking: boolean;
}

function formatLastUsed(value: string | null): string {
  if (value === null) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}

export function DeviceRow({
  device,
  onConfirmRevoke,
  isRevoking,
}: DeviceRowProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const label = device.device_label ?? copy.settings.devices.deviceLabelFallback;
  const kindLabel =
    device.kind === 'session'
      ? copy.settings.devices.kindSession
      : copy.settings.devices.kindRefresh;

  return (
    <li
      data-testid="device-row"
      className="flex flex-col gap-2 border-b border-border-subtle py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-body-strong text-text-primary">{label}</span>
          {device.current && (
            <Badge data-testid="device-current-badge">
              {copy.settings.devices.currentBadge}
            </Badge>
          )}
        </div>
        <div className="text-caption text-text-muted">
          <span>{kindLabel}</span>
          <span aria-hidden="true"> · </span>
          <span>
            {copy.settings.devices.columnLastUsed}: {formatLastUsed(device.last_used_at)}
          </span>
        </div>
      </div>
      <div>
        {!device.current && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" type="button">
                {copy.settings.devices.revokeButton}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{copy.settings.devices.revokeDialogTitle}</DialogTitle>
                <DialogDescription>
                  {copy.settings.devices.revokeDialogBody}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">
                    {copy.settings.devices.revokeDialogCancel}
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  type="button"
                  disabled={isRevoking}
                  aria-busy={isRevoking}
                  onClick={() => {
                    onConfirmRevoke(device.id);
                    setOpen(false);
                  }}
                >
                  {copy.settings.devices.revokeDialogConfirm}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </li>
  );
}
